"use strict";

import {Router} from './Router.js';
import {AppApi} from './AppApi.js';
import {AppStorage} from './AppStorage.js';
import {AppAnalytics} from './app/AppAnalytics.js';
import {AppCookieConsent} from './app/AppCookieConsent.js';
import {AppAiWidget} from './app/AppAiWidget.js';
import Scrollyeah from "./modules/Scrollyeah";
import {buildCupertinoPaneSettings} from './modules/CupertinoPaneHelper';

export const App = {

	params: {},

	/**
	 * Параметры по-умолчанию, если не определены при инициализации
	 */
	_defaultParams: {
		isAuthorized: false,
		isAdmin: false,
		isDev: false,
		phpTimeZoneOffset: 0,
		skipPaymentRetryLoader: false,
	},

	modals: [],
	sliders: [],

	/** @var AppApiComponent */
	api: null,

	/** @var AppStorageComponent */
	storage: null,

	/** @var AppBasket */
	basket: null,

	/** @var AppWishlist */
	wishlist: null,

	/** @var AppAnalytics */
	analytics: null,

	breakpoint: 768,

	timer: null,

	autocompleteTimeout: 300,

	/**
	 * @returns {*|void}
	 */
	isDev: function () {
		return this.getParam('isDev');
	},

	isDevEx: function () {
		return this.getParam('isDevEx');
	},

	isDevDomain: function () {
		return this.getParam('isDevDomain');
	},

	/**
	 * @returns {*|void}
	 */
	isAdmin: function () {
		return this.getParam('isAdmin');
	},

	/**
	 * @returns {*|void}
	 */
	isAuthorized: function () {
		return this.getParam('isAuthorized');
	},

	/**
	 * Текущий язык витрины (ru/kk/en) для AJAX-запросов.
	 * @returns {string}
	 */
	getAppLang: function () {
		let lang = '',
			match = (document.body.className || '').match(/\blang_([a-z]{2})\b/);

		if (match) lang = match[1];

		if (!lang) lang = document.documentElement.lang || 'ru';

		if (lang === 'kz') lang = 'kk';

		return (lang === 'kk' || lang === 'en') ? lang : 'ru';
	},

	/** UI-строки toast/форм: ключ → {ru, kk, en} */
	_messages: {
		basketItemAdded: {ru: 'Товар добавлен в корзину', kk: 'Тауар себетке қосылды', en: 'Added to your cart'},
		articleCopied: {ru: 'Артикул скопирован', kk: 'Зат нөмірі көшірілді', en: 'Item number copied'},
		whatsappMessageFailed: {ru: 'Не удалось сформировать сообщение для WhatsApp', kk: 'WhatsApp үшін хабарламаны құру мүмкін болмады', en: 'Couldn\'t prepare the WhatsApp message'},
		requestError: {ru: 'Произошла ошибка при выполнении запроса', kk: 'Сұрауды орындау кезінде қате орын алды', en: 'Something went wrong with this request'},
		genericError: {ru: 'Произошла ошибка. Попробуйте позже.', kk: 'Қате орын алды. Кейінірек қайталап көріңіз.', en: 'Something went wrong. Please try again later.'},
		addressSaved: {ru: 'Адрес сохранён', kk: 'Мекенжай сақталды', en: 'Address saved'},
		profileSaved: {ru: 'Данные профиля сохранены', kk: 'Профиль деректері сақталды', en: 'Profile saved'},
		deliveryUpdateError: {ru: 'Ошибка при обновлении способа доставки', kk: 'Жеткізу тәсілін жаңарту кезінде қате', en: 'Couldn\'t update the delivery method'},
		paymentUpdateError: {ru: 'Ошибка при обновлении способа оплаты', kk: 'Төлем тәсілін жаңарту кезінде қате', en: 'Couldn\'t update the payment method'},
		addressUpdateError: {ru: 'Ошибка при обновлении адреса', kk: 'Мекенжайды жаңарту кезінде қате', en: 'Couldn\'t update the address'},
		orderSaveError: {ru: 'Ошибка при сохранении заказа', kk: 'Тапсырысты сақтау кезінде қате', en: 'Couldn\'t save the order'},
		paymentLinkError: {ru: 'Не удалось получить ссылку для оплаты', kk: 'Төлем сілтемесін алу мүмкін болмады', en: 'Couldn\'t get the payment link'},
		paymentMethodsLoadError: {ru: 'Ошибка загрузки способов оплаты', kk: 'Төлем тәсілдерін жүктеу қатесі', en: 'Couldn\'t load payment methods'},
		orderCreateError: {ru: 'Ошибка создания заказа', kk: 'Тапсырысты жасау қатесі', en: 'Couldn\'t place the order'},
		timeExpired: {ru: 'Время вышло', kk: 'Уақыт аяқталды', en: 'Time\'s up'},
		paymentMethodChanged: {ru: 'Способ оплаты изменен', kk: 'Төлем тәсілі өзгертілді', en: 'Payment method updated'},
		tagsReset: {ru: 'Рубрики сброшены', kk: 'Бөлімдер қалпына келтірілді', en: 'Topics cleared'},
		undo: {ru: 'Отменить', kk: 'Болдырмау', en: 'Undo'},
		close: {ru: 'Закрыть', kk: 'Жабу', en: 'Close'},
		buy: {ru: 'Купить', kk: 'Сатып алу', en: 'Buy'},
		addToWishlist: {ru: 'Добавить в избранное', kk: 'Таңдаулыға қосу', en: 'Add to wishlist'},
		removeFromBasket: {ru: 'Убрать из корзины', kk: 'Себеттен алып тастау', en: 'Remove from cart'},
		goToBasket: {ru: 'Перейти в корзину', kk: 'Себетке өту', en: 'Go to cart'},
		continueShopping: {ru: 'Продолжить покупки', kk: 'Сатып алуды жалғастыру', en: 'Continue shopping'},
		inBasket: {ru: 'В корзине', kk: 'Себетте', en: 'In cart'},
		login: {ru: 'Войти', kk: 'Кіру', en: 'Sign in'},
		personal: {ru: 'Личный кабинет', kk: 'Жеке кабинет', en: 'Account'},
		scrollUp: {ru: 'Наверх', kk: 'Жоғары', en: 'Back to top'},
		prev: {ru: 'Предыдущие', kk: 'Алдыңғы', en: 'Previous'},
		next: {ru: 'Следующие', kk: 'Келесі', en: 'Next'},
		outOfStock: {ru: 'Нет в наличии', kk: 'Қолда жоқ', en: 'Out of stock'},
		payOrder: {ru: 'Оплатить заказ', kk: 'Тапсырысты төлеу', en: 'Pay for this order'},
		changePayment: {ru: 'Изменить способ оплаты', kk: 'Төлем әдісін өзгерту', en: 'Change payment method'},
		formRequired: {ru: 'Заполните поле', kk: 'Өрісті толтырыңыз', en: 'Please fill in this field'},
		formRemote: {ru: 'Пожалуйста, введите корректное значение', kk: 'Дұрыс мәнді енгізіңіз', en: 'Please enter a valid value'},
		formEmail: {ru: 'Пожалуйста, укажите корректную почту', kk: 'Дұрыс электрондық поштаны көрсетіңіз', en: 'Please enter a valid email address'},
		formPattern: {ru: 'Неверный формат', kk: 'Формат қате', en: 'Invalid format'},
		formUrl: {ru: 'Введите корректный URL', kk: 'Дұрыс URL енгізіңіз', en: 'Please enter a valid URL'},
		formDate: {ru: 'Введите корректную дату', kk: 'Дұрыс күнді енгізіңіз', en: 'Please enter a valid date'},
		formDateIso: {ru: 'Введите корректную дату в формате ISO', kk: 'ISO форматында дұрыс күнді енгізіңіз', en: 'Please enter a valid date in ISO format'},
		formNumber: {ru: 'Введите число', kk: 'Сан енгізіңіз', en: 'Please enter a number'},
		formDigits: {ru: 'Только цифры', kk: 'Тек цифрлар', en: 'Digits only'},
		formCreditcard: {ru: 'Введите правильный номер', kk: 'Дұрыс карта нөмірін енгізіңіз', en: 'Please enter a valid card number'},
		formEqualTo: {ru: 'Пароли должны совпадать', kk: 'Құпия сөздер сәйкес келуі керек', en: 'Passwords must match'},
		formExtension: {ru: 'Пожалуйста, выберите файл с правильным расширением', kk: 'Дұрыс кеңейтімі бар файлды таңдаңыз', en: 'Please choose a file with a valid extension'},
		formMaxlength: {ru: 'Не больше {0} символов', kk: '{0} таңбадан артық емес', en: 'No more than {0} characters'},
		formMinlength: {ru: 'Не меньше {0} символов', kk: '{0} таңбадан кем емес', en: 'At least {0} characters'},
		formRangelength: {ru: 'От {0} до {1} символов', kk: '{0}–{1} таңба', en: '{0} to {1} characters'},
		formRange: {ru: 'От {0} до {1}', kk: '{0}–{1} аралығында', en: 'Enter a value from {0} to {1}'},
		formMax: {ru: 'Меньшее или равное {0}', kk: '{0}-ден кіші немесе тең', en: 'Must be {0} or less'},
		formMin: {ru: 'Большее или равное {0}', kk: '{0}-ден үлкен немесе тең', en: 'Must be {0} or more'},
		formStep: {ru: 'Введите значение кратное {0}.', kk: '{0}-ге еселік мән енгізіңіз.', en: 'Enter a value in steps of {0}.'},
		formDateExt: {ru: 'Введите корректную датульку', kk: 'Дұрыс күнді енгізіңіз', en: 'Please enter a valid date'},
		formPhone: {ru: 'Введите корректный номер телефона', kk: 'Дұрыс телефон нөмірін енгізіңіз', en: 'Please enter a valid phone number'},
		formSmsAgreement: {ru: 'Примите согласие на получение СМС-сообщений', kk: 'СМС-хабарламаларды алуға келісім беріңіз', en: 'Please agree to receive SMS messages'},
	},

	/**
	 * UI-строка toast/форм по body.lang_* / html[lang].
	 * @param {string} key
	 * @returns {string}
	 */
	getMessage: function (key) {
		let map = this._messages && this._messages[key];
		if (!map) return key;

		let lang = this.getAppLang();

		return map[lang] || map.ru || key;
	},

	/**
	 * @returns {boolean}
	 */
	isInitialized: function () {
		return this._isInitialized;
	},

	/**
	 * @param parameters
	 */
	init: function (parameters) {
		this.params = parameters;
		this.params.sessid = BX.bitrix_sessid();

		this.api = new AppApi({
			'sessid': this.getParam('sessid')
		});

		this.storage = new AppStorage({
			'type': 'localStorage'
		});

		if (window.frameCacheVars !== undefined) {
			BX.addCustomEvent('onFrameDataReceived', () => this._init());
		} else {
			BX.ready(() => this._init());
		}
	},

	/**
	 * void
	 * @private
	 */
	_init: function () {
		this.initAnalytics();
		this.initAiWidget();
		this.initCookieConsent();
		this.initEvents();
		this.initStickyHeader();
		this.initHeaderSearch();
		this.loadHeaderMenu();
		this.initBasket();
		this.initWishlist();
		this.initVideo();
		this.initCatalogSorter();
		this.initSmartFilter();
		this.initNews();
		this.initCertificateForm();
		this.initSubscribe();
		this.initOrderPaymentRetry();
		this.initOrder();
		this.initAddressEdit();

		document.dispatchEvent(new CustomEvent('AppInit', {bubbles: true, detail: this}));

		this._isInitialized = true;
		if (App.isDev()) console.log('App init done!');
	},


	/**
	 * @param element
	 * @param eventType
	 * @param selector
	 * @param callback
	 */
	delegate: function (element, eventType, selector, callback) {
		element.addEventListener(eventType, (event) => {
			let el = event.target.closest(selector);
			if (el) callback.call(el, event);
		});
	},

	/**
	 * @param mixedValue
	 * @returns {string}
	 */
	serialize: function (mixedValue) {
		let app = this;
		let _getType = function (inp) {
				let type = typeof inp, match, key;
				if (type === 'object' && !inp) return 'null';
				if (type === 'object') {
					if (!inp.constructor) return 'object';
					let cons = inp.constructor.toString();
					if (match = cons.match(/(\w+)\(/)) cons = match[1].toLowerCase();
					let types = ['boolean', 'number', 'string', 'array'];
					for (key in types) if (cons === types[key]) {
						type = types[key];
						break;
					}
				}

				return type;
			},
			type = _getType(mixedValue),
			val,
			ktype = '';

		switch (type) {
			case 'function':
				val = '';
				break;
			case 'undefined':
				val = 'N';
				break;
			case 'boolean':
				val = 'b:' + (mixedValue ? '1' : '0');
				break;
			case 'number':
				val = (Math.round(mixedValue) === mixedValue ? 'i' : 'd') + ':' + mixedValue;
				break;
			case 'string':
				let length = 0;
				for (let i = 0; mixedValue.length > i; i++) {
					if (/[абвгдеёжзийклмнопрстуфхцчшщъыьэюяАБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ]/.test(mixedValue[i])) length += 2; else length++;
				}
				val = 's:' + length + ':"' + mixedValue + '"';
				break;
			case 'array':
			case 'object':
				val = 'a';

				let count = 0,
					vals = '',
					okey, key;

				for (key in mixedValue) {
					ktype = _getType(mixedValue[key]);
					if (ktype === 'function') continue;
					okey = (key.match(/^[0-9]+$/) ? parseInt(key) : key);
					vals += app.serialize(okey) + app.serialize(mixedValue[key]);
					count++;
				}

				val += ':' + count + ':{' + vals + '}';
				break;
		}

		if (type !== 'object' && type !== 'array') val += ';';

		return val;
	},

	/**
	 * @param data
	 * @returns {*}
	 */
	unserialize: function (data) {
		let that = this,
			utf8Overhead = function (chr) {
				let code = chr.charCodeAt(0);
				if (code < 0x0080) return 0;
				if (code < 0x0800) return 1;
				return 2;
			},
			error = function (type, msg, filename, line) {
				throw new window[type](msg, filename, line);
			},
			read_until = function (data, offset, stopchr) {
				var i = 2, buf = [], chr = data.slice(offset, offset + 1);

				while (chr != stopchr) {
					if ((i + offset) > data.length) {
						error('Error', 'Invalid');
					}
					buf.push(chr);
					chr = data.slice(offset + (i - 1), offset + i);
					i += 1;
				}
				return [buf.length, buf.join('')];
			},
			read_chrs = function (data, offset, length) {
				let i, chr, buf;

				buf = [];
				for (i = 0; i < length; i++) {
					chr = data.slice(offset + (i - 1), offset + i);
					buf.push(chr);
					length -= utf8Overhead(chr);
				}
				return [buf.length, buf.join('')];
			},
			_unserialize = function (data, offset) {
				let dtype, dataoffset, keyandchrs, keys,
					readdata, readData, ccount, stringlength,
					i, key, kprops, kchrs, vprops, vchrs, value,
					chrs = 0,
					typeconvert = function (x) {
						return x;
					},
					readArray = function () {
						readdata = {};

						keyandchrs = read_until(data, dataoffset, ':');
						chrs = keyandchrs[0];
						keys = keyandchrs[1];
						dataoffset += chrs + 2;

						for (i = 0; i < parseInt(keys, 10); i++) {
							kprops = _unserialize(data, dataoffset);
							kchrs = kprops[1];
							key = kprops[2];
							dataoffset += kchrs;

							vprops = _unserialize(data, dataoffset);
							vchrs = vprops[1];
							value = vprops[2];
							dataoffset += vchrs;

							readdata[key] = value;
						}
					};

				if (!offset) offset = 0;
				dtype = (data.slice(offset, offset + 1)).toLowerCase();
				dataoffset = offset + 2;

				switch (dtype) {
					case 'i':
						typeconvert = function (x) {
							return parseInt(x, 10);
						};
						readData = read_until(data, dataoffset, ';');
						chrs = readData[0];
						readdata = readData[1];
						dataoffset += chrs + 1;
						break;
					case 'b':
						typeconvert = function (x) {
							return parseInt(x, 10) !== 0;
						};
						readData = read_until(data, dataoffset, ';');
						chrs = readData[0];
						readdata = readData[1];
						dataoffset += chrs + 1;
						break;
					case 'd':
						typeconvert = function (x) {
							return parseFloat(x);
						};
						readData = read_until(data, dataoffset, ';');
						chrs = readData[0];
						readdata = readData[1];
						dataoffset += chrs + 1;
						break;
					case 'n':
						readdata = null;
						break;
					case 's':
						ccount = read_until(data, dataoffset, ':');
						chrs = ccount[0];
						stringlength = ccount[1];
						dataoffset += chrs + 2;

						readData = read_chrs(data, dataoffset + 1, parseInt(stringlength, 10));
						chrs = readData[0];
						readdata = readData[1];
						dataoffset += chrs + 2;
						if (chrs != parseInt(stringlength, 10) && chrs != readdata.length) {
							error('SyntaxError', 'String length mismatch');
						}
						break;
					case 'a':
						readArray();
						dataoffset += 1;
						break;
					case 'o':
						ccount = read_until(data, dataoffset, ':');
						dataoffset += ccount[0] + 2;

						ccount = read_until(data, dataoffset, '"');
						dataoffset += ccount[0] + 2;

						readArray();
						dataoffset += 1;
						break;
					default:
						error('SyntaxError', 'Unknown / Unhandled data type(s): ' + dtype);
						break;
				}
				return [dtype, dataoffset - offset, typeconvert(readdata)];
			}
		;

		return _unserialize((data + ''), 0)[2];
	},

	/**
	 * @param num
	 * @param one
	 * @param two
	 * @param five
	 * @returns {*}
	 */
	strEnding: function (num, one, two, five) {
		let _num = parseInt(num) % 100,
			ending;

		if (_num >= 11 && _num <= 19) {
			ending = five;
		} else {
			switch (_num % 10) {
				case (1):
					ending = one;
					break;
				case (2):
				case (3):
				case (4):
					ending = two;
					break;
				default:
					ending = five;
			}
		}

		return ending;
	},

	/**
	 * void
	 */
	supportsPassive() {
		let supportsPassive = false;
		try {
			let opts = Object.defineProperty({}, 'passive', {
				get: function () {
					supportsPassive = true;
				}
			});
			window.addEventListener('testPassive', null, opts);
			window.removeEventListener('testPassive', null, opts);
		} catch (e) {
		}

		return supportsPassive;
	},

	/**
	 * @param paramName
	 * @returns {*|void}
	 */
	getParam: function (paramName) {
		let paramVal = this.params[paramName];

		if (typeof paramVal === 'undefined') {
			paramVal = this._defaultParams[paramName];
		}

		return (typeof paramVal !== 'undefined' ? paramVal : console.error('Parameter with name: "' + paramName + '" not found!'));
	},

	/**
	 * @param paramName
	 * @param paramValue
	 * @returns {*|void}
	 */
	setParam: function (paramName, paramValue) {
		this.params[paramName] = paramValue;
	},

	/**
	 * Отправить в цель в Я.Метрику
	 * @param goal Идентификатор цели
	 * @param params Доп. параметры цели
	 */
	reachGoal: function (goal, params) {
		//Для админов и дев пользователей евенты передавать не нужно
		if (this.isDevEx() === true || this.isAdmin() === true) return;

		// for dedug use add $_GET param _ym_debug=1
		let yaCounterId = this.getParam('yaCounterId'),
			counterName = 'yaCounter' + yaCounterId;

		if (!window[counterName]) {
			document.addEventListener('yacounter' + yaCounterId + 'inited', () => {
				App.reachGoal(goal, params)
			});
			return;
		}

		if (typeof window[counterName] !== 'undefined' && goal) {
			let goals = goal.split(',');
			for (let i in goals) {
				if (goals.hasOwnProperty(i)) {
					window[counterName].reachGoal(goals[i], params, (e) => {
						document.dispatchEvent(new CustomEvent('AppReachGoal', {
							bubbles: true,
							detail: {goal: goals[i]}
						}));
					});
				}
			}
		}
	},

	/**
	 * Отправить событие в гугл аналитику
	 * @param {string} category Например 'Site'
	 * @param {string} action
	 * @param {string} label
	 */
	gaSendEvent: function (category, action, label) {
		//Для админов и дев пользователей евенты передавать не нужно
		if (this.isDevEx() === true || this.isAdmin() === true) return;

		if (typeof ga !== 'undefined') {
			let data = {
				hitType: 'event',
				eventCategory: category,
				eventAction: action
			};

			if (label) {
				data.eventLabel = label
			}

			ga('send', data);
		}
	},

	/**
	 * Единая точка: dataLayer (синхронно) + черновик для events.trimiata.ru + опциональная навигация после flush.
	 * @param {Object} payload Объект для window.dataLayer.push
	 * @param {{navigateTo?: string}|undefined} options
	 */
	raiseEvent: function (payload, options) {
		let navigateTo = options && options.navigateTo ? String(options.navigateTo) : '';

		//Для админов и дев пользователей евенты передавать не нужно
		if (this.isDevEx() === true || this.isAdmin() === true) {
			if (navigateTo) window.location.assign(navigateTo);

			return;
		}

		if (this.analytics) {
			this.analytics.raiseEvent(payload, options);
		}
	},

	/**
	 * void
	 */
	initEvents: function () {
		if (this.isInitialized()) return;

		if (App.isDev()) console.log('start: initEventsListeners');

		let app = this,
			api = this.api;

		//Ленивая загрузка
		let lazyloadImages = document.querySelectorAll('.lazyload');
		if (lazyloadImages.length) {
			Router.assets.lazysizes().then(module => {
			});
		}

		//Scrollyeah
		let scrollyeahBlock = document.querySelectorAll('.scrollyeah');
		if (scrollyeahBlock.length) {
			Router.assets.Scrollyeah().then(module => {
				window.Scrollyeah = module.Scrollyeah;
				if (document.readyState === 'loading') {
					document.addEventListener('DOMContentLoaded', () => Scrollyeah.initAll());
				} else {
					Scrollyeah.initAll();
				}
			});
		}

		//Фэнсибокс: чанк один раз, модуль сам вешает клик на [data-fancybox]
		let fancyboxLoading = false,
			ensureFancybox = () => {
				if (window.Fancybox || fancyboxLoading) return;

				fancyboxLoading = true;
				Router.assets.Fancybox().then(module => {
					if (!window.Fancybox) window.Fancybox = module.Fancybox;
				});
			};
		if (document.querySelector('[data-fancybox]')) ensureFancybox();

		//Параллакс изображения
		let paralaxImages = document.querySelectorAll('[data-parallax=true]');
		if (paralaxImages.length) {
			Router.assets.Rellax().then(module => {
				if (!window.Rellax) window.Rellax = module.default;

				new Rellax('[data-parallax=true]', {
					speed: 1,
					center: true,
					wrapper: null,
					round: true,
					vertical: true,
					horizontal: false
				});
			});
		}

		import(/* webpackChunkName: 'js/assets/initialize' *//* webpackMode: 'lazy-once' *//* webpackPreload: true */ '../../node_modules/jquery.initialize').then(module => {

			//Замена стандартных селектов
			$.initialize('[data-type=nice-select]', (i, el) => {
				Router.assets.NiceSelect().then(niceSelectModule => {
					if (!window.NiceSelect) window.NiceSelect = niceSelectModule.default;
					new NiceSelect(el, {searchable: false, placeholder: ' '});
				});
			});

			//Кнопка поделиться
			$.initialize('[data-role=share-page]', (i, el) => {
				app.initYandexShare(el);
			});

			//Маски ввода телефонов
			$.initialize('input.phone, input[type=tel], input[data-role=phone], input[name=phone]', (i, el) => {
				this.initPhoneInputMask(el);
			});

			//Поповеры
			$.initialize('[data-bs-toggle=popover]', (i, el) => {
				new bootstrap.Popover(el, {trigger: 'hover focus'});
			});

			//Даты
			$.initialize('input.date, input[type=date], input[data-role=date], input[data-type=date], input[name=date]', (i, el) => {
				Router.assets.Imask().then(imaskModule => {
					IMask(
						el,
						{
							mask: Date,
							min: new Date(1900, 0, 1),
							lazy: true
						}
					)
				});
			})

			//Подсказки ввода email
			$.initialize('[data-suggestion=email]', (i, el) => {
				this.initEmailSuggestion(el);
			});

			//Подсказки ввода адреса
			$.initialize('[data-suggestion=address]', (i, el) => {
				this.initAddressSuggestion(el);
			});

			//Слайдеры в списках товаров
			$.initialize('[data-role=image-slider]', (i, el) => {
				app.initSlidesGallery(el, {});
			});

			//Фэнсибокс в подгружаемом попапе (offcanvas, CupertinoPane)
			$.initialize('[data-fancybox]', () => ensureFancybox());

			//Слайдер на детальной странице и в подгружаемом aside
			$.initialize('[data-role=detail-image-slider]', (i, detailSlider) => {
				app.initSlidesGallery(detailSlider).then(slider => {
					let wrapp = detailSlider.closest('[data-role=slider-wrapp]');
					if (slider && wrapp) {
						let thumbnails = wrapp.querySelector('[data-role=thumbnails-slider]');
						if (thumbnails) {
							app.initSlidesGallery(thumbnails, {axis: 'y', align: 'start', syncWith: slider});
						}
					}
				});
			});

			//Зум fancybox (включая подгрузку)
			$.initialize('.fancybox__container .fancybox__content .fancybox__image', (i, el) => {
				el.parentElement.style.setProperty('--bg', 'url(\'' + el.src + '\')');
				el.parentElement.style.setProperty('--bg-h', '1000px');
				el.parentElement.style.setProperty('--bg-w', '1000px');
				el.addEventListener('pointermove', (e) => {
					let {target} = e,
						x = (e.offsetX / target.offsetWidth) * 100,
						y = (e.offsetY / target.offsetHeight) * 100;
					target.parentElement.style.backgroundPosition = `${x}% ${y}%`;
				});
			});

			//Кнопка прокрутки страницы наверх
			$.initialize('[data-role=scroll-up-btn]', (i, el) => {
				//Прокрутка вверх
				el.addEventListener('click', () => window.scrollTo({top: 0, behavior: 'smooth'}));
				//Плавное появление кнопки при прокрутке больше одного экрана
				window.addEventListener('scroll', () => {
					let scrollTop = document.documentElement.scrollTop || document.body.scrollTop,
						viewportHeight = window.innerHeight,
						step = 2.5; //Шаг изменения прозрачности
					if (scrollTop > viewportHeight) {
						el.style.opacity = Math.min((scrollTop - viewportHeight) / step * 0.01, 1);
						el.style.pointerEvents = 'all';
					} else {
						el.style.opacity = 0;
						el.style.pointerEvents = 'none';
					}
				});
			});
		});

		//Клик по ссылке открывающей лоадер
		/* app.delegate(document, 'click', '[data-role=show-loader]', (e) => {
			let btn = e.target;
			if (btn.tagName === 'A' && btn.getAttribute('href')) app.showLoader();
		}); */

		//Клик на элемент списка для ecommerce
		app.delegate(document, 'click', '[data-role=catalog-element-link]', (e) => {
			e.stopPropagation();

			//Цель для Я.Метрики
			if (!App || !App.isInitialized()) {
				document.addEventListener('AppInit', function (e) {
					e.detail.reachGoal('PRODUCT_CLICK');
				});
			} else {
				App.reachGoal('PRODUCT_CLICK');
			}

			let link = e.target.closest('a[href]'),
				list = e.target.closest('[data-role=catalog-products-list]'),
				item = e.target.closest('[data-role=catalog-list-item]');


			console.log('link', link);
			console.log('list', list);
			console.log('item', item);

			if (link && list && item) {
				let listName = list.getAttribute('data-list-name'),
					categoryName = item.getAttribute('data-item-category'),
					name = item.getAttribute('data-item-name'),
					id = item.getAttribute('data-item-article'),
					price = item.getAttribute('data-item-price'),
					position = item.getAttribute('data-item-position');

				//Отправляем данные (синхронный dataLayer + отложенный переход — см. AppAnalytics)
				if (listName && categoryName && id && position) {
					e.preventDefault();

					App.raiseEvent({
						event: 'productClick',
						ecommerce: {
							currencyCode: App.getParam('currencyCode'),
							click: {
								actionField: {list: listName},
								products: [{
									name: name,
									id: id,
									price: price,
									category: categoryName,
									position: position
								}]
							}
						}
					}, {navigateTo: link.getAttribute('href')});

					return false;
				}
			}
		});

		//Ленивая загрузка видео
		document.addEventListener('lazyloaded', function (e) {
			let video = e.target;
			if (video.tagName === 'VIDEO') {
				let sources = video.querySelectorAll('source');
				if (sources) sources.forEach(source => {
					let src = source.getAttribute('data-src');
					if (src) source.src = source.getAttribute('data-src');
				});
				video.load();
			}
		});

		//Смена иконки WhatsApp
		let whatsappIcons = document.querySelectorAll('[data-role=whatsapp-icon]');
		if (whatsappIcons) whatsappIcons.forEach(icon => {
			let normal = icon.querySelector('[data-role=whatsapp-icon-normal]'),
				write = icon.querySelector('[data-role=whatsapp-icon-write]'),
				hiddenClass = 'icon__brand_hidden',
				visible = true;

			if (!normal || !write) return;

			setInterval(() => {
				if (visible) {
					normal.classList.add(hiddenClass);
					write.classList.remove(hiddenClass);
				} else {
					normal.classList.remove(hiddenClass);
					write.classList.add(hiddenClass);
				}
				visible = !visible;
			}, 6666);
		})

		//Автоплей при ховере на видео
		let autoplayVideos = document.querySelectorAll('[data-role=autoplay-video]');
		if (autoplayVideos) autoplayVideos.forEach(video => {
			let wrapper = video.closest('.swiper-slide');
			if (wrapper) wrapper.addEventListener('mouseover', () => {
				video.play().catch(err => {
				});
			});
		})

		//Прокрутка колесом мыши вертикальных скроллов
		let verticalWheelScroll = document.querySelectorAll('[data-role=vertical-mouse-scroll]');
		if (verticalWheelScroll) verticalWheelScroll.forEach(container => {
			container.addEventListener('wheel', function (e) {
				if (e.deltaY !== 0) {
					e.preventDefault();
					container.scrollLeft = container.scrollLeft + e.deltaY;
				}
			}, {passive: false});

			let isDown = false,
				startX = 0,
				startScrollLeft = 0,
				lockedAxis = null, // 'x' | 'y' | null
				LOCK_THRESHOLD = 6, // пикселей, чтобы понять направление
				onPointerDown = (e) => {
					// только ЛКМ или тач/перо
					if (e.pointerType === 'mouse' && e.button !== 0) return;
					isDown = true;
					lockedAxis = null;
					startX = e.clientX;
					startScrollLeft = container.scrollLeft;
					container.setPointerCapture?.(e.pointerId);
					container.style.cursor = 'grabbing';
				},
				onPointerMove = (e) => {
					if (!isDown) return;
					let dx = e.clientX - startX;
					if (!lockedAxis && Math.abs(dx) >= LOCK_THRESHOLD) lockedAxis = 'x';
					if (lockedAxis === 'x') {
						e.preventDefault(); // важно: чтобы страница не скроллилась
						container.scrollLeft = startScrollLeft - dx;
					}
				},
				onPointerUp = (e) => {
					if (!isDown) return;
					isDown = false;
					lockedAxis = null;
					container.releasePointerCapture?.(e.pointerId);
					container.style.cursor = 'grab';
				};

			container.style.cursor = 'grab';
			container.style.userSelect = 'none';
			container.addEventListener('pointerdown', onPointerDown, {passive: true});
			container.addEventListener('pointermove', onPointerMove, {passive: false});
			container.addEventListener('pointerup', onPointerUp, {passive: true});
			container.addEventListener('pointercancel', onPointerUp, {passive: true});
			container.addEventListener('pointerleave', onPointerUp, {passive: true});
		});

		//Аякс модальные окна
		app.delegate(document, 'click', '[data-toggle=ajax-modal]', (e) => {
			e.preventDefault();

			let button = e.target.closest('[data-toggle=ajax-modal]'),
				path = button.getAttribute('data-path'),
				params = button.getAttribute('data-params') || {},
				addToCacheAttr = button.getAttribute('data-add-to-cache'),
				cacheType = button.getAttribute('data-cache-type') || 'runtime',
				loaderType = button.getAttribute('data-loader-type') || 'button';

			//Определяем, нужно ли кеширование: если указан атрибут, проверяем, не загружена ли уже модалка
			let addToCache = false;
			if (addToCacheAttr === 'true' || addToCacheAttr === '1') {
				let modalHash = encodeURI(app.serialize({path: path, params: params, showModal: true}));
				addToCache = !app.modals[modalHash]; //Кешируем только при первой загрузке
			}

			//Выбор типа лоадера в зависимости от data-атрибута
			if (loaderType === 'global') app.showLoader('_loaded'); else app.showBtnLoading(button);

			app.loadAjaxModal(path, params, true, addToCache, cacheType).then(() => {
				document.dispatchEvent(new Event('_loaded'));
				if (loaderType !== 'global') button.dispatchEvent(new Event('_loaded'));
			});
		});

		//Аякс сайдбары
		app.delegate(document, 'click', '[data-toggle=ajax-aside]', (e) => {
			e.preventDefault();

			let button = e.target.closest('[data-toggle=ajax-aside]'),
				path = button.getAttribute('data-path'),
				contentPageUrl = button.getAttribute('data-content-page'),
				params = button.getAttribute('data-params') || {};

			//Определяем контекст: если клик изнутри quick_buy - помечаем для особой обработки
			let insideQuickBuy = !!button.closest('.offcanvas-quick_buy');
			if (insideQuickBuy) {
				params._fromQuickBuy = true;
			}

			app.showBtnLoading(button);
			app.loadAjaxAside((contentPageUrl ? contentPageUrl : path), (contentPageUrl ? 'sitePage' : 'api'), params, true).then(() => {
				button.dispatchEvent(new Event('_loaded'));
			});

			return false;
		});

		//Отслеживание изменений класса у body
		const observer = new MutationObserver(app.updateBodyPadding);
		observer.observe(document.body, {attributes: true, attributeFilter: ['class']});
		app.updateBodyPadding();

		//Подгрузка элементов списка по кнопке "Загрузить еще"
		app.delegate(document, 'click', '[data-role=load-more]', (e) => {
			e.preventDefault();

			let button = e.target.closest('[data-role=load-more]'),
				params = button.getAttribute('data-params') || {};

			if (!params || !button) return;

			let _params = app.unserialize(decodeURIComponent(params));
			if (_params && _params.path && _params.itemsWrapp) {
				app.showBtnLoading(button);
				app.loadMore(_params.path, params, true).then(() => {
					button.dispatchEvent(new Event('_loaded'));
				});
			}
		});

		//Клик по кнопке Оформить в корзине (для авторизованных пользователей)
		app.delegate(document, 'click', '[data-role=go-to-checkout]', function (e) {
			//Если это не ссылка (span с ajax-modal), обработка происходит через стандартный обработчик [data-toggle=ajax-modal]
			if (this.tagName !== 'A') return;

			//Для ссылок просто показываем лоадер на кнопке
			app.showBtnLoading(this);
		});

		//Клик по кнопке Показать код карты
		app.delegate(document, 'click', '[data-role=load-more]', (e) => {
			e.preventDefault();

			let button = e.target.closest('[data-role=load-more]'),
				params = button.getAttribute('data-params') || {};

			if (!params || !button) return;

			let _params = app.unserialize(decodeURIComponent(params));
			if (_params && _params.path && _params.itemsWrapp) {
				app.showBtnLoading(button);
				app.loadMore(_params.path, params, true).then(() => {
					button.dispatchEvent(new Event('_loaded'));
				});
			}
		});

		//Замена настроек свайпера в завсимости от разрешения
		window.addEventListener('resize', () => {
			app.swiperBreakpoints();
		}, app.supportsPassive ? {passive: true} : false);
		app.swiperBreakpoints();

		//Открытие мобильного меню личного кабинета
		let openMobileFilterBtn = document.querySelectorAll('[data-role=account-mobile-menu-opener]');
		if (openMobileFilterBtn) openMobileFilterBtn.forEach(_btn => {
			_btn.addEventListener('click', () => {
				if (!app.accountMobileMenu) return;
				app.accountMobileMenu.present({animate: true});
			});
		});

		//Сноска золотой карты на /loyalty/: подробнее / скрыть
		app.delegate(document, 'click', '[data-role=loyalty-gold-note-toggle]', (e) => {
			e.preventDefault();
			let button = e.target.closest('[data-role=loyalty-gold-note-toggle]');
			if (!button) return;
			let note = button.closest('[data-role=loyalty-gold-note]');
			if (!note) return;
			let expanded = note.classList.toggle('is-expanded');
			button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
			button.textContent = expanded
				? (button.getAttribute('data-less') || '')
				: (button.getAttribute('data-more') || '');
		});

		//Копирование текста
		app.delegate(document, 'click', '[data-role=copy-text]', (e) => {
			let button = e.target.closest('[data-role=copy-text]');
			if (button) {
				let text = button.getAttribute('data-text');
				if (text && text.length) app.copyTextToClipboard(text).then(() => app.showToast(app.getMessage('articleCopied')));
			}
		});

		//Цель Я.Метрики
		app.delegate(document, 'click', '[data-reach-goal]', (e) => {
			if (App.isDevEx() === true || App.isAdmin() === true) return;

			let goal = e.target.getAttribute('data-reach-goal');
			if (goal && goal.length) {
				app.reachGoal(goal);
			}
		});

		//Цель Google Analytics
		app.delegate(document, 'click', '[data-analytics-event]', (e) => {
			if (App.isDevEx() === true || App.isAdmin() === true) return;

			let analyticsEvent = e.target.getAttribute('data-analytics-event');
			if (analyticsEvent && analyticsEvent.length) {
				app.gaSendEvent('Site', analyticsEvent);
			}
		});

		if (App.isDev()) console.log('end: initEventsListeners');
	},

	/**
	 * @param text
	 * @returns {Promise<boolean>}
	 */
	copyTextToClipboard: async function (text) {
		if (navigator.clipboard && navigator.clipboard.writeText) {

			try { //Современный способ через Clipboard API
				await navigator.clipboard.writeText(text);
				return true;
			} catch (err) {
			}
		} else { //Фоллбэк для старых браузеров
			const textarea = document.createElement('textarea');
			textarea.value = text;
			textarea.style.position = 'fixed';
			textarea.style.top = 0;
			textarea.style.left = 0;
			textarea.style.opacity = '0';
			document.body.appendChild(textarea);
			textarea.focus();
			textarea.select();

			try {
				return document.execCommand('copy');
			} catch (err) {
			}

			document.body.removeChild(textarea);
		}

		return false;
	},

	/**
	 * @param message
	 * @param type
	 */
	showToast(message, type = 'ok') {
		let component = this;

		let icon = '';
		switch (type) {
			case 'ok':
				icon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none"><circle cx="11.9999" cy="12.9706" r="10.0853" fill="white"/><path d="M18.4138 8.89066L10.254 17.0505L5.58643 12.3829" stroke="#8E7B6C" stroke-miterlimit="10"/></svg>';
				break;
			case 'error':
				icon = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none"><path d="M2.0315 22C1.83399 22 1.65927 21.9537 1.50735 21.8612C1.35542 21.7686 1.23725 21.6465 1.15285 21.4947C1.06169 21.3455 1.01105 21.1842 1.00092 21.0107C0.992477 20.8365 1.04228 20.661 1.15031 20.4842L11.1143 3.50527C11.2223 3.32851 11.3523 3.2001 11.5042 3.12006C11.6562 3.04002 11.8216 3 12.0005 3C12.1795 3 12.3445 3.04002 12.4956 3.12006C12.6467 3.2001 12.7766 3.32851 12.8855 3.50527L22.8508 20.4842C22.9588 20.661 23.0082 20.8361 22.9989 21.0095C22.9905 21.1846 22.9398 21.3463 22.847 21.4947C22.7626 21.6465 22.6444 21.7686 22.4925 21.8612C22.3405 21.9537 22.1662 22 21.9696 22H2.0315ZM2.44171 20.7493H21.5594L12.0005 4.49078L2.44171 20.7493ZM12.0005 19.0184C12.2217 19.0184 12.4069 18.9446 12.5563 18.7971C12.7057 18.6495 12.78 18.4665 12.7792 18.248C12.7783 18.0296 12.7036 17.847 12.5551 17.7002C12.4065 17.5535 12.2217 17.4793 12.0005 17.4776C11.7794 17.476 11.5946 17.5497 11.446 17.699C11.2975 17.8482 11.2228 18.0312 11.2219 18.248C11.2211 18.4648 11.2958 18.6478 11.446 18.7971C11.5962 18.9463 11.7811 19.0201 12.0005 19.0184ZM12.0005 16.2282C12.1812 16.2282 12.3318 16.1682 12.4525 16.0481C12.5732 15.9281 12.6336 15.7796 12.6336 15.6029V10.6003C12.6336 10.4227 12.5728 10.2738 12.4513 10.1538C12.3297 10.0337 12.1791 9.97409 11.9993 9.97492C11.8195 9.97576 11.6693 10.0354 11.5486 10.1538C11.4279 10.2722 11.3675 10.421 11.3675 10.6003V15.6029C11.3675 15.7796 11.4283 15.9281 11.5498 16.0481C11.6714 16.1682 11.822 16.2282 12.0018 16.2282" fill="white"/></svg>';
				break;
			default:
				break;
		}

		Router.assets.bs5().then(bs5Module => {
			if (!window.bs5) window.bs5 = bs5Module.default;
			component._toast = new bs5.Toast({
				body: '<div class="toast_body__content"><div class="d-flex"><div class="toast_body__content__icon">' + icon + '</div><div>' + message + '</div></div>',
				btnCloseWhite: true,
				delay: 5000,
				placement: 'top-center',
				className: 'start-50 translate-middle-x'
			});
			component._toast.show();
		});
	},

	/**
	 * @param el
	 */
	initPhoneInputMask: function (el) {
		Router.assets.Imask().then(imaskModule => {
			let _masked = IMask(el, {
				mask: [
					{
						mask: '+0 (000) 000-00-00',
						startsWith: '7',
						code: 'ru',
						lazy: true,
						overwrite: 'shift',
						prepare: value => value
					},
					{
						mask: '+0 (000) 000-00-00',
						startsWith: '8',
						code: 'ru_8',
						lazy: true,
						overwrite: 'shift',
						prepare: value => value
					},
					{
						mask: '+0 (000) 000-00-00',
						startsWith: '9',
						code: 'ru_9',
						lazy: true,
						overwrite: 'shift',
						prepare: value => value
					},
					{
						mask: /[+]/,
						startsWith: '',
						code: 'default',
						lazy: true,
						overwrite: 'shift',
						prepare: value => value
					},
				],
				dispatch: function (appended, dynamicMasked) {
					let number = (dynamicMasked.value + appended).replace(/\D/g, '');
					let selectedMask = dynamicMasked.compiledMasks.find(function (m) {
						return number.indexOf(m.startsWith) === 0;
					});
					//Если не найдена маска и введена цифра "7", "8" или "9" - выбираем соответствующую маску
					if (!selectedMask && /^[789]$/.test(appended)) {
						if (appended === '7' || appended === '8') {
							selectedMask = dynamicMasked.compiledMasks.find(m => m.code === 'ru');
						} else if (appended === '9') {
							selectedMask = dynamicMasked.compiledMasks.find(m => m.code === 'ru_9');
						}
					}
					return selectedMask;
				},
				commit: (value, masked) => {
					/* field.setSelectionRange(masked._value.length, masked._value.length); */
				}
			});

			//Обработка ввода для удобного ввода российских номеров
			_masked.on('accept', () => {
				let unmaskedValue = _masked.unmaskedValue,
					currentMask = _masked.masked.currentMask,
					value = _masked.value,
					digitsOnly = unmaskedValue.replace(/\D/g, '');

				//Если ввели только "+" - добавляем "7"
				if (currentMask.code === 'default' && value === '+') {
					_masked.unmaskedValue = '7';
					return;
				}

				//Если ввели "8" - заменяем на "7"
				if (currentMask.code === 'ru_8' && digitsOnly === '8') {
					_masked.unmaskedValue = '7';
					return;
				}

				//Если ввели "9" в пустое поле - добавляем "7" перед "9"
				if (currentMask.code === 'ru_9' && digitsOnly === '9') {
					_masked.unmaskedValue = '79';
					return;
				}

				//Если ввели "7" без "+" в начале (маска ru выбрана, но значение не начинается с "+")
				if (currentMask.code === 'ru' && digitsOnly === '7' && !value.startsWith('+')) {
					//Устанавливаем значение напрямую для корректного форматирования
					setTimeout(() => {
						if (_masked.value && !_masked.value.startsWith('+')) _masked.value = '+7';
					}, 0);
					return;
				}
			});
		});
	},

	/**
	 * @param ms
	 * @returns {Promise<unknown>}
	 */
	timeout(ms) {
		return new Promise(resolve => {
			this.timer = setTimeout(resolve, ms)
		});
	},

	/**
	 * @param _inp
	 */
	initEmailSuggestion: function (_inp) {
		if (!_inp || !_inp.name) return;

		let app = this,
			api = app.api;

		Router.assets.Autocomplete().then(autocompleteModule => {
			if (!window.autoComplete) window.autoComplete = autocompleteModule.default;

			_inp.autoComplete = new autoComplete({
				selector: '[name="' + _inp.name + '"][data-suggestion=email]',
				threshold: 2,
				data: {
					src: async (query) => {
						let data = {
							q: query
						};

						await app.timeout(app.autocompleteTimeout);
						let result = await api.apiRequest('suggestion/email.php', 'POST', data).then((response) => {
							if (!response || !response.items) return [{}];
							return response.items.suggestions.map((_item) => {
								return {
									value: _item.value,
								}
							});
						}).catch(function (err) {
							if (app.isDev()) console.error('Suggestion error [' + err.status + ']: ' + err.statusText);
						});

						return await result;
					},
					keys: ['value']
				},
				query: (input) => {
					if (!!app.timer) clearTimeout(app.timer);
					return input ? app.prepareAutoCompleteQuery(input, true) : '';
				},
				resultsList: {
					tag: 'ul',
					id: 'emails_list',
					class: 'list autocomplete_list',
					destination: () => {
						let wrapp = _inp.closest('[data-role=form-field-wrapp]');
						if (wrapp) {
							let destination = wrapp.querySelector('[data-role=suggestion-wrapp]');
							if (destination) return destination;
						}
						return wrapp;
					},
					position: 'beforeend',
					maxResults: 3,
				},
				resultItem: {
					tag: 'li',
					class: 'option',
					highlight: true,
				},
				events: {
					input: {
						selection: (event) => {
							let value = event.detail.selection.value.value;
							_inp.value = value;
							_inp.dispatchEvent(new Event('input', {bubbles: true}));
							return false;
						},
						focus(e) {
							const inputValue = _inp.autoComplete.input.value;
							if (inputValue.length) _inp.autoComplete.start();
							app.showAutoCompleteList(e)
						},
						blur(e) {
							app.hideAutoCompleteList(e)
						},
						open(e) {
							app.showAutoCompleteList(e)
						},
						close(e) {
							app.hideAutoCompleteList(e)
						}
					}
				}
			});
		});
	},

	/**
	 * @param _inp
	 */
	initAddressSuggestion: function (_inp) {
		if (!_inp || !_inp.name) return;

		let app = this,
			api = app.api;

		Router.assets.Autocomplete().then(autocompleteModule => {
			if (!window.autoComplete) window.autoComplete = autocompleteModule.default;

			_inp.autoComplete = new autoComplete({
				selector: '[name="' + _inp.name + '"][data-suggestion=address]',
				threshold: 2,
				data: {
					src: async (query) => {
						let locaition = _inp.getAttribute('data-location');
						if (!locaition) locaition = app.getParam('locationCode');

						let data = {
							kladr: locaition,
							q: query
						};

						await app.timeout(app.autocompleteTimeout);
						let result = await api.apiRequest('suggestion/address.php', 'POST', data).then((response) => {
							if (!response || !response.items) return [{}];

							return response.items;
						}).catch(function (err) {
							if (app.isDev()) console.error('Suggestion error [' + err.status + ']: ' + err.statusText);
						});

						return await result;
					},
					keys: ['value']
				},
				query: (input) => {
					if (!!app.timer) clearTimeout(app.timer);
					return input ? app.prepareAutoCompleteQuery(input) : '';
				},
				resultsList: {
					tag: 'ul',
					id: 'address_list',
					class: 'list autocomplete_list',
					destination: () => {
						let wrapp = _inp.closest('[data-role=form-field-wrapp]');
						if (wrapp) {
							let destination = wrapp.querySelector('[data-role=suggestion-wrapp]');
							if (destination) return destination;
						}
						return wrapp;
					},
					position: 'beforeend',
					maxResults: 3,
				},
				resultItem: {
					tag: 'li',
					class: 'option',
					highlight: true,
				},
				events: {
					input: {
						selection: (event) => {
							let value = event.detail.selection.value.value;
							_inp.value = value;
							_inp.dispatchEvent(new Event('input', {bubbles: true}));
							return false;
						},
						focus(e) {
							const inputValue = _inp.autoComplete.input.value;
							if (inputValue.length) _inp.autoComplete.start();
							app.showAutoCompleteList(e)
						},
						blur(e) {
							app.hideAutoCompleteList(e)
						},
						open(e) {
							app.showAutoCompleteList(e)
						},
						close(e) {
							app.hideAutoCompleteList(e)
						}
					}
				}
			});
		});
	},

	/**
	 * @param e
	 */
	showAutoCompleteList(e) {
		let input = e.target;

		if (input.value.length) {
			let wrapper = input.closest('[data-role=form-field-wrapp]');
			if (wrapper) {
				let list = wrapper.querySelector('.autocomplete_list');
				if (list && list.querySelector('[role="option"]')) list.removeAttribute('hidden');
				wrapper.classList.add('open');
			}
		}
	},

	/**
	 * @param e
	 */
	hideAutoCompleteList(e) {
		let input = e.target,
			wrapper = input.closest('[data-role=form-field-wrapp]');
		if (wrapper) {
			let list = wrapper.querySelector('.autocomplete_list');
			if (list && list.querySelector('[role="option"]')) list.setAttribute('hidden', 'hidden');
			wrapper.classList.remove('open');
		}
	},

	/**
	 * @param el
	 * @param _params
	 * @returns {*}
	 */
	initSlidesGallery: function (el, _params) {
		let defaultParams = {
				draggable: true,
			},
			params = {...defaultParams, ..._params};

		return Router.assets.EmblaCarousel().then(module => {
			if (!window.EmblaCarousel) window.EmblaCarousel = module.default;

			let slider = EmblaCarousel(el, params);

			//Смена фото при наведении
			let overlayPagination = el.querySelector('[data-role=slider-overlay-pagination]');
			if (overlayPagination) {
				let scrollToSlide = (e) => {
						if (e.target.dataset.slide) slider.scrollTo(e.target.dataset.slide, true)
					},
					scrollToFirst = () => slider.scrollTo(0, true);

				overlayPagination.addEventListener('mouseover', scrollToSlide);
				overlayPagination.addEventListener('mouseleave', scrollToFirst);
				slider.on('destroy', () => {
					overlayPagination.removeEventListener('mouseover', scrollToSlide);
					overlayPagination.removeEventListener('mouseleave', scrollToFirst);
				});
			}

			//Пагинация
			let container = el.closest('[data-role=slider-wrapp]');
			if (container) {
				let pagination = container.querySelector('[data-role=slider-pagination]');
				if (pagination) {
					let scrollToSlide = ({target}) => {
							if (target.getAttribute('data-role') !== 'pagination-item') return;
							if (target.dataset.slide) slider.scrollTo(target.dataset.slide, true);
						},
						setActive = () => {
							let activeClass = 'active',
								currentActive = pagination.querySelector('.' + activeClass);
							if (currentActive) currentActive.classList.remove(activeClass);
							let newActive = pagination.querySelector(`[data-role=pagination-item][data-slide="${slider.selectedScrollSnap()}"]`);
							if (newActive) newActive.classList.add(activeClass);
						};

					pagination.addEventListener('click', scrollToSlide);
					slider.on('init', setActive);
					slider.on('select', setActive);
					slider.on('destroy', () => pagination.removeEventListener('click', scrollToSlide));
				}
			}

			//Синхронизация с другим слайдером
			if (params.syncWith) {
				let syncSlider = params.syncWith,
					getSlideClickListener = (() => {
						let listeners = new Map();
						return (idx) => {
							if (listeners.has(idx)) return listeners.get(idx);
							let listener = (e) => {
								e.stopPropagation();
								syncSlider.scrollTo(idx);
							};
							listeners.set(idx, listener);
							return listener;
						};
					})(),
					updateActiveSlide = () => {
						let previous = syncSlider.previousScrollSnap(),
							selected = syncSlider.selectedScrollSnap()
						slider.slideNodes()[previous].classList.remove('active');
						slider.slideNodes()[selected].classList.add('active');
					},
					sync = () => {
						slider.scrollTo(syncSlider.selectedScrollSnap());
						updateActiveSlide();
					};

				syncSlider.on('select', sync);
				slider.on('init', sync);

				slider.on('select', () => {
					if (syncSlider.selectedScrollSnap() >= slider.selectedScrollSnap()) return;
					syncSlider.scrollTo(slider.selectedScrollSnap());
					updateActiveSlide();
				});

				slider.on('init', () => {
					slider.slideNodes().forEach((slide, index) => {
						let listener = getSlideClickListener(index);
						slide.addEventListener('click', listener, true);
					});
				});

				slider.on('destroy', () => {
					slider.slideNodes().forEach((slide, index) => {
						let listener = getSlideClickListener(index);
						slide.removeEventListener('click', listener, true);
						slide.classList.remove('active');
					});
				});
			}

			return slider;
		});
	},

	/**
	 * void
	 */
	initVideo() {
		let video = document.querySelector('video.product_gallery__image_detail[data-role=autoplay-video]');

		if (video) video.play().catch(err => {
		});
	},

	/**
	 * @param selector
	 * @param params
	 * @returns {*}
	 */
	initSwiper(selector, params) {
		let app = this,
			name = 'swiper_' + selector,
			container = document.querySelector(selector);

		return Router.assets.Swiper().then(module => {
			if (!window.Swiper) window.Swiper = module.Swiper;

			if (container && container.closest('.catalog__products_gallery_swap')) {
				//Loop только при ≥3 слайдах — иначе Swiper warning
				params.loop = container.querySelectorAll('.swiper-slide').length > 2;
				params.on = {};
				params.on.activeIndexChange = (swiper) => {
					let breakpointCenter = 768;
					if (swiper.el) {
						let active = swiper.el.querySelector('.swiper-slide' + (window.innerWidth <= breakpointCenter ? '.swiper-slide-active' : '.swiper-slide-next'));
						if (active) {
							let video = active.querySelector('video');
							if (video) {
								//Запуск видео только после загрузки
								video.addEventListener('canplay', function onCanPlay() {
									video.removeEventListener('canplay', onCanPlay);
									video.play().catch(err => {
									});
								})
							}
						}
						let other = swiper.el.querySelectorAll('.swiper-slide:not(.swiper-slide-prev):not(.swiper-slide-active):not(.swiper-slide-next)');
						if (other) other.forEach(el => {
							let video = el.querySelector('video');
							if (video) video.pause();
						});
					}
				};
				if (container.closest('.catalog__products_slider_big_centered')) {
					params.on.beforeTransitionStart = (swiper, speed, internal) => {
						swiper.el.style.height = swiper.el.getBoundingClientRect().height + 'px';
					};
					params.on.transitionEnd = (swiper) => {
						swiper.el.style.height = null
					};
				} else {
					params.autoHeight = true;
				}

				let breakpointCenter = 768;
				if (window.innerWidth <= breakpointCenter) {
					params.centeredSlides = true;
					params.centeredSlidesBounds = true;
				}
			}

			let slider = new Swiper(selector, params);
			app.sliders.push(slider);
			window[name] = slider;

			return window[name];

		});
	},

	/**
	 * Загрузка главного меню через AJAX
	 * @returns {Promise<void>}
	 */
	loadHeaderMenu: function () {
		let app = this,
			menuContainer = document.querySelector('[data-role=header-menu-container]');

		if (!menuContainer) return Promise.resolve();

		let lang = app.getAppLang();

		//Меню уже загружено для текущего языка
		if (menuContainer.getAttribute('data-menu-loaded') === 'true'
			&& menuContainer.getAttribute('data-menu-lang') === lang) {
			return Promise.resolve();
		}

		return this.api.apiRequest('modal/menu/', 'GET', {lang: lang}, true, 'runtime').then((result) => {
			if (result && result.success) {
				let dataTypes = ['html', 'script', 'storage'];
				for (let i in dataTypes) {
					if (dataTypes.hasOwnProperty(i)) {
						let dataType = dataTypes[i];
						if (result[dataType]) {
							switch (dataType) {
								case 'html':
									menuContainer.innerHTML = result[dataType];
									menuContainer.setAttribute('data-menu-loaded', 'true');
									menuContainer.setAttribute('data-menu-lang', lang);
									break;
								case 'storage':
									for (let type in result[dataType])
										if (result[dataType].hasOwnProperty(type))
											app.storage.set(type, result[dataType][type]);
									break;
								case 'script':
									setTimeout(() => eval(result[dataType]), 200);
									break;
								default:
									break;
							}
						}
					}
				}

				document.dispatchEvent(new CustomEvent('HeaderMenuLoaded', {bubbles: true, detail: app}));
			}
		}).catch((err) => {
			console.error('Ошибка загрузки главного меню:', err);
		});
	},

	/**
	 * void
	 */
	initSectionsMenu: function () {
		let component = this;

		//Переинициализируем меню, если оно уже было создано (например, после AJAX загрузки)
		Router.modules.AppSectionsMenu().then(module => {
			component.sectionsMenu = new module.AppSectionsMenu(component);
		});
	},

	/**
	 * void
	 */
	initAnalytics: function () {
		let component = this;

		if (component.isInitialized()) return;

		component.analytics = new AppAnalytics(component);
	},

	/**
	 * Слушатель postMessage AI-виджета ImShop. В основном бандле: сообщение может прийти с любой страницы.
	 */
	initAiWidget: function () {
		AppAiWidget.init(this);
	},

	/**
	 * Баннер cookie: клики живут и до App.init, здесь только догоняем узел
	 */
	initCookieConsent: function () {
		AppCookieConsent.init();
	},

	/**
	 * void
	 */
	initBasket: function () {
		let component = this;

		if (component.isInitialized()) return;

		Router.modules.AppBasket().then(module => {
			component.basket = new module.AppBasket(component);
		});
	},

	/**
	 * void
	 */
	initWishlist: function () {
		let component = this;

		if (component.isInitialized()) return;

		Router.modules.AppWishlist().then(module => {
			component.wishlist = new module.AppWishlist(component);
		});
	},

	/**
	 * void
	 */
	initCatalogSorter: function () {
		let component = this;

		if (component.isInitialized()) return;

		let catalogSorter = document.querySelector('[data-role=catalog-sorter]');
		if (catalogSorter) {
			Router.modules.AppCatalogSorter().then(module => {
				component.AppCatalogSorter = new module.AppCatalogSorter(component);
			});
		}
	},

	/**
	 * void
	 */
	initSmartFilter: function () {
		let component = this;

		let smartFilter = document.querySelector('[data-role=catalog-smart-filter]');
		if (smartFilter) {
			Router.modules.AppSmartFilter().then(module => {
				component.AppSmartFilter = new module.AppSmartFilter(component);
			});
		}
	},

	/**
	 * void
	 */
	initHeaderMenu: function () {
		let component = this;

		//Переинициализируем меню, если оно уже было создано (например, после AJAX загрузки
		Router.modules.AppHeaderMenu().then(module => {
			component.headerMenu = new module.AppHeaderMenu(component);
		});
	},

	/**
	 * void
	 */
	initStickyHeader() {
		let component = this,
			headerSelector = '.header',
			bottomPointSelector = '[data-role=header-trigger]',
			header = document.querySelector(headerSelector);

		if (header === null || header.classList.contains('header_no_sticky')) return;

		let bottomPoint = document.querySelector(bottomPointSelector);
		if (bottomPoint === null) return;

		if (headerSelector && bottomPointSelector) {
			Router.modules.AppStickyHeader().then(module => {
				component.stickyHeader = new module.AppStickyHeader(component, headerSelector, bottomPointSelector);
			});
		}
	},

	/**
	 * void
	 */
	initHeaderSearch: function () {
		let component = this;

		if (component.isInitialized()) return;

		let headerSearch = document.querySelector('[data-role=header-search]');
		if (headerSearch) {
			Router.modules.AppHeaderSearch().then(module => {
				component.headerSearch = new module.AppHeaderSearch(component);
			});
		}
	},

	/**
	 * void
	 */
	initCertificateForm: function () {
		let component = this;

		if (component.isInitialized()) return;

		let certificateForm = document.querySelector('[data-role=certificate-form-tabs]');
		if (certificateForm) {
			Router.modules.AppCertificateForm().then(module => {
				component.AppCertificateForm = new module.AppCertificateForm(component);
			});
		}
	},

	/**
	 * void
	 */
	initAuth: function () {
		let component = this;

		if (!this.getParam('isAuthorized')) {
			let containers = document.querySelectorAll('[data-modal-role=auth]');
			if (containers) containers.forEach(_container => {
				if (!this.auth) this.auth = {};

				Router.modules.AppAuthorization().then(module => {
					this.auth[_container.getAttribute('data-auth-template')] = new module.AppAuthorization(component);
				});
			})
		}
	},

	/**
	 * void
	 */
	initAddressEdit: function () {
		let component = this;

		let addressEdit = document.querySelector('[data-role=account-address-edit]');
		if (addressEdit) {
			Router.modules.AppAddressEdit().then(module => {
				component.AppAddressEdit = new module.AppAddressEdit(component);
			});
		}
	},

	/**
	 * void
	 */
	initLocations: function () {
		let component = this;

		Router.modules.AppLocations().then(module => {
			component.AppNews = new module.AppLocations(component);
		});
	},

	/**
	 * void
	 */
	initNews: function () {
		let component = this;

		if (component.isInitialized()) return;

		let newsList = document.querySelector('[data-role=news-list]');
		if (newsList) {
			let tagsList = document.querySelector('[data-role=news-tags-list]');
			if (tagsList) {
				Router.modules.AppNews().then(module => {
					component.AppNews = new module.AppNews(component);
				});
			}
		}
	},

	/**
	 * void
	 */
	initSubscribe: function () {
		let component = this;

		if (component.isInitialized()) return;

		let subscribeForm = document.querySelector('[data-role=news-subscribe-form]');
		if (subscribeForm) {
			Router.modules.AppSubscribe().then(module => {
				component.AppSubscribe = new module.AppSubscribe(component);
			});
		}
	},

	/**
	 * void
	 */
	initOrderPaymentRetry: function () {
		let component = this;

		if (component.isInitialized()) return;

		if (component.shouldSkipPaymentRetryLoader()) {
			let paymentRetry = document.querySelector('[data-role=order-payment-retry]');
			if (paymentRetry) {
				paymentRetry.remove();
			}

			return;
		}

		let paymentRetry = document.querySelector('[data-role=order-payment-retry]');
		if (paymentRetry) {
			Router.modules.AppOrderPaymentRetry().then(module => {
				component.AppOrderPaymentRetry = new module.AppOrderPaymentRetry(component);
			});
		}
	},

	/**
	 * Возврат с FreedomPay на /thankyou/ (pg_payment_id) — без плашки отложенной оплаты.
	 * @returns {boolean}
	 */
	shouldSkipPaymentRetryLoader: function () {
		if (this.getParam('skipPaymentRetryLoader')) return true;

		let pgPaymentId = new URLSearchParams(window.location.search).get('pg_payment_id');

		return pgPaymentId !== null && String(pgPaymentId).trim() !== '';
	},

	/**
	 * void
	 */
	initOrder: function () {
		let component = this;

		if (component.isInitialized()) return;

		let order = document.querySelector('[data-role="order-checkout"]');
		if (order) {
			Router.modules.AppOrder().then(module => {
				component.AppOrder = new module.AppOrder(component);
			});
		}
	},

	/**
	 * void
	 */
	initProfileEdit: function (container) {
		let component = this;

		let wrapp = document.querySelector('#' + container);
		if (wrapp) {
			let profileForm = wrapp.querySelector('[data-role=account-profile-edit]');
			if (profileForm) {
				Router.modules.AppProfileEdit().then(module => {
					component.AppProfileEdit = new module.AppProfileEdit(container, component);
				});
			}
		}
	},

	/**
	 * @param container
	 * @param params
	 * @returns {AppForm|null}
	 */
	initForm: function (container, params) {
		if (!container) return null;

		if (document.querySelector('#' + container)) {
			Router.modules.AppForm().then(module => {
				if (!window.AppForm) window.AppForm = module.AppForm;

				return new module.AppForm(container, params);
			});
		}

		return null;
	},

	/**
	 * Яндекс.Карты: маркеры по data-geo; на /contacts/ — data-shops + попап (.contacts_map) по макету.
	 * @param selector
	 */
	initMap: function (selector) {
		let mapEl = document.querySelector(selector);
		if (!mapEl) return;

		let geoRaw = mapEl.dataset.geo;
		if (!geoRaw) return;

		let coordinates;
		try {
			coordinates = JSON.parse(geoRaw);
		} catch (e) {
			return;
		}

		if (!coordinates || !coordinates.length) return;

		let shops = [];
		if (mapEl.dataset.shops) {
			try {
				shops = JSON.parse(mapEl.dataset.shops);
			} catch (e) {
				shops = [];
			}
		}

		if (!shops.length) {
			coordinates.forEach((c) => {
				shops.push({
					coords: c,
					title: '',
					address: '',
					schedule: '',
					phone: '',
					email: '',
					url: '',
					flagship: false,
				});
			});
		}

		let defaultXmlId = String(mapEl.dataset.defaultXmlid || '').trim();
		let defaultIndex = -1;
		if (defaultXmlId) {
			defaultIndex = shops.findIndex((s) => String(s && s.xml_id || '').toUpperCase() === defaultXmlId.toUpperCase());
		}

		let fillPopup = (shop) => {
			return;

		};

		let hidePopup = () => {
			return;
		};

		let {ymaps} = window,
			init = async () => {
				let id = mapEl.id || 'map';
				let map = new ymaps.Map(
					id,
					{
						center: [37.64, 55.76],
						zoom: 7,
						controls: [],
					},
					{
						maxZoom: 17,
					},
				);

				let ua = navigator.userAgent || '',
					isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
					touchScroll = isIos || window.matchMedia('(pointer: coarse)').matches;

				//Стандартное API 2.1: без зума колесом; dblclick и кнопки ZoomControl оставляем
				map.behaviors.disable('scrollZoom');
				//Тач: свайп и pinch скроллят страницу, карту не двигают. Мышь на десктопе остаётся.
				if (touchScroll) {
					map.behaviors.disable('drag');
					map.behaviors.disable('multiTouch');
				}
				map.controls.add('zoomControl', { float: 'right' });

				//iOS: CSS filter на WebGL canvas не рисуется — тёплый слой между тайлами и маркерами
				if (isIos && map.panes && typeof map.panes.get === 'function') {
					mapEl.classList.add('ymap_tone_host');
					let groundPane = map.panes.get('ground'),
						groundEl = groundPane && typeof groundPane.getElement === 'function' ? groundPane.getElement() : null,
						panesRoot = groundEl && groundEl.parentNode;
					if (panesRoot && !panesRoot.querySelector('.ymap_tone')) {
						let tone = document.createElement('div');
						tone.className = 'ymap_tone';
						let groundZ = parseInt(window.getComputedStyle(groundEl).zIndex, 10),
							placesPane = map.panes.get('places'),
							placesEl = placesPane && typeof placesPane.getElement === 'function' ? placesPane.getElement() : null,
							placesZ = placesEl ? parseInt(window.getComputedStyle(placesEl).zIndex, 10) : NaN,
							z = Number.isFinite(groundZ) ? groundZ + 1 : 101;
						if (Number.isFinite(placesZ) && z >= placesZ) z = placesZ - 1;
						tone.style.zIndex = String(z);
						panesRoot.insertBefore(tone, groundEl.nextSibling);
					}
				}

				let allowPageScroll = () => {
					if (!touchScroll) return;
					let setPanY = (el) => {
						if (!el || !el.style) return;
						el.style.touchAction = 'pan-y';
					};
					setPanY(mapEl);
					if (map.container && typeof map.container.getElement === 'function') setPanY(map.container.getElement());
					mapEl.querySelectorAll('.ymaps-2-1-79-events-pane').forEach(setPanY);
				};
				allowPageScroll();

				let useCluster = coordinates.length > 1;
				//Кольцо кластера на карте магазинов (группа точек)
				let clusterer = useCluster
					? new ymaps.Clusterer({
						groupByCoordinates: false,
						clusterIconColor: '#D4A67C',
					})
					: null;
				let placemarks = [];

				coordinates.forEach((item, index) => {
					let shop = shops[index] || null;
					let props = {
						balloonContent: shop && shop.balloon ? String(shop.balloon || '').replace(/\n/g, '<br />') : '',
						hintContent: String((shop && (shop.address || shop.title)) || '').trim(),
					};

					let placemark = new ymaps.Placemark(item, props, {
						iconLayout: 'default#image',
						iconImageHref: '/images/svg/geo.svg',
						iconImageSize: [81, 97],
						iconImageOffset: [-40.5, -97],
					});

					placemark.events.add('click', () => {
						if (props.balloonContent) placemark.balloon.open();
					});

					placemarks.push(placemark);
				});

				if (clusterer) {
					clusterer.add(placemarks);
					map.geoObjects.add(clusterer);
				} else {
					placemarks.forEach((pm) => map.geoObjects.add(pm));
				}

				let margin = [0, 400, 0, 0],
					mobileMediaQuery = window.matchMedia('(max-width: 480px)');
				if (mobileMediaQuery.matches) margin = [0];

				let boundsReady = map.setBounds(map.geoObjects.getBounds(), {checkZoomRange: true, zoomMargin: margin});

				//Список магазинов: после границ отдаляем ровно на один шаг. Детальная карта не внутри .contacts_map
				if (mapEl.closest('.contacts_map')) {
					let zoomOut = () => {
						let range = map.zoomRange && typeof map.zoomRange.getCurrent === 'function' ? map.zoomRange.getCurrent() : null,
							minZoom = Array.isArray(range) ? range[0] : 0,
							maxZoom = Number(map.options.get('maxZoom')),
							zoom = map.getZoom() - 1;
						if (!Number.isFinite(maxZoom)) maxZoom = 17;
						if (!Number.isFinite(zoom)) return;
						if (zoom < minZoom) zoom = minZoom;
						if (zoom > maxZoom) zoom = maxZoom;
						map.setZoom(zoom, {checkZoomRange: true});
					};
					if (boundsReady && typeof boundsReady.then === 'function') boundsReady.then(zoomOut);
					else zoomOut();
				}

				let openIndex = defaultIndex >= 0 ? defaultIndex : 0;
				let openPm = placemarks[openIndex] || null;
				//data-balloon-open="0" — детальная: балун только по клику. На таче детальной тоже не открываем.
				let skipAutoBalloon = mapEl.dataset.balloonOpen === '0'
					|| (mapEl.classList.contains('store_detail__map_canvas') && touchScroll);
				if (openPm && openPm.balloon && !skipAutoBalloon) {
					openPm.balloon.open();
				}

				//WebKit после setBounds оставляет контейнер 0×0 — пересчитываем вьюпорт
				let fitViewport = () => {
					if (!map || !map.container || typeof map.container.fitToViewport !== 'function') return;
					if (!mapEl.isConnected) return;
					try {
						map.container.fitToViewport();
					} catch (e) {
						//Карта уже уничтожена
					}
				};
				requestAnimationFrame(() => {
					fitViewport();
					allowPageScroll();
				});
				window.addEventListener('load', () => {
					fitViewport();
					allowPageScroll();
				}, {once: true});
			};

		ymaps.ready(() => {
			//WebKit часто игнорирует timeout у requestIdleCallback — init так и не стартует
			let started = false,
				start = () => {
					if (started) return;
					started = true;
					init();
				};

			if (typeof window.requestIdleCallback !== 'function') {
				setTimeout(start, 0);

				return;
			}

			let idleId = window.requestIdleCallback(start, {timeout: 1500});
			setTimeout(() => {
				if (started) return;
				if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleId);
				start();
			}, 1600);
		});
	},

	/**
	 * void
	 */
	initYandexShare: function (el) {
		if (!el) return;

		if (window.Ya && window.Ya.share2 && typeof window.Ya.share2 === 'function') {
			window.Ya.share2(el, {
				content: {
					url: el.dataset.url,
					title: el.dataset.title,
					description: el.dataset.desc,
					image: el.dataset.image
				},
				theme: {
					services: 'vkontakte,telegram,whatsapp,odnoklassniki',
					bare: false,
					curtain: true,
					moreButtonType: 'short',
					useLinks: true,
					limit: 0
				}
			});

			//Обработчик клика на элемент _share для показа/скрытия попапа
			let popupOpenedClass = 'ya_share_popup_show';
			el.addEventListener('click', function (e) {
				e.preventDefault();
				e.stopPropagation();
				if (el.classList.contains(popupOpenedClass)) el.classList.remove(popupOpenedClass);
				else el.classList.add(popupOpenedClass);
			});
			document.addEventListener('click', function (e) {
				if (!el.contains(e.target)) el.classList.remove(popupOpenedClass);
			});
		}
	},

	/**
	 * @param productId
	 * @param elementId ID модели при добавлении с сайта
	 * @param quantity (сейчас всегда 1 в запросе)
	 * @param xmlId XML_ID предложения: в запрос action, xmlId и quantity 1, без productId
	 * @returns {*}
	 */
	addToBasket: function (productId, elementId, quantity, xmlId) {
		let data = {
			'action': 'add',
			'productId': productId,
			'elementId': elementId,
			'quantity': 1,
		};
		//productId=0 не валидируем: сервер резолвит xmlId раньше проверки товара
		if (xmlId) {
			data = {
				'action': 'add',
				'xmlId': xmlId,
				'quantity': 1,
			};
		}

		return this.api.basket(data, 'POST');
	},

	/**
	 * @param productId
	 * @returns {*}
	 */
	removeFromBasket: function (productId) {
		return this.api.basket({
			'action': 'delete',
			'productId': productId,
		}, 'POST');
	},

	/**
	 * @param coupon
	 * @returns {*}
	 */
	addBasketCoupon: function (coupon) {
		return this.api.basket({
			'action': 'addCoupon',
			'coupon': coupon,
		}, 'POST');
	},

	/**
	 * @param coupon
	 * @returns {*}
	 */
	removeBasketCoupon: function (coupon) {
		return this.api.basket({
			'action': 'removeCoupon',
			'coupon': coupon,
		}, 'POST');
	},

	/**
	 * @param orderId
	 * @param paySystemId
	 * @returns {*}
	 */
	changeOrderPayment: function (orderId, paySystemId) {
		return this.api.order({
			'action': 'changePaySystem',
			'id': orderId,
			'paySystemId': paySystemId
		}, 'POST');
	},

	/**
	 * @param orderId
	 * @returns {*}
	 */
	loadOrderPaymentRetry: function (orderId = 0) {
		if (this.shouldSkipPaymentRetryLoader()) return Promise.resolve({success: true, html: ''});

		return this.api.order({
			'action': 'loadPaymentRetryComponent',
			'id': orderId,
		}, 'POST');
	},

	/**
	 * @returns {*}
	 */
	loadCurrentOrdersData: function () {
		return this.api.order({
			'action': 'loadCurrentOrdersComponent',
		}, 'POST');
	},

	/**
	 * @param orderId
	 * @param sessid
	 * @returns {*}
	 */
	cancelOrder: function (orderId, sessid) {
		return this.api.order({
			'action': 'cancelOrder',
			'id': orderId,
			'sessid': sessid
		}, 'POST');
	},

	/**
	 * @returns {*}
	 */
	getHeaderWishlist: function () {
		return this.api.wishlist({
			'action': 'getWishlist'
		}, 'POST');
	},

	/**
	 * @param productId
	 * @returns {*}
	 */
	addToWishlist: function (productId) {
		return this.api.wishlist({
			'action': 'add',
			'productId': productId,
		}, 'POST');
	},

	/**
	 * @param productId
	 * @returns {*}
	 */
	deleteFromWishlist: function (productId) {
		return this.api.wishlist({
			'action': 'delete',
			'productId': productId
		}, 'POST');
	},


	/**
	 * @param productXmlId
	 * @returns {*}
	 */
	getProductDelivery: function (productXmlId) {
		return this.api.delivery({
			'action': 'get',
			'productXmlId': productXmlId,
		}, 'POST');
	},

	/**
	 * @param productXmlId
	 * @returns {*}
	 */
	getProductInserts: function (productXmlId) {
		return this.api.inserts({
			'action': 'get',
			'productXmlId': productXmlId,
		}, 'POST');
	},

	/**
	 * void
	 */
	swiperBreakpoints: function () {
		let app = this,
			breakpointCenter = 768;

		if (!app.sliders) return;

		if (window.innerWidth >= breakpointCenter) {
			app.sliders.forEach(slider => {
				if (slider.initialized === true) {
					let container = slider.el;
					if (container && container.classList.contains('catalog__products_slider__list') && container.closest('.catalog__products_gallery_swap')) {
						if (container.classList.contains('catalog__products_slider__list_centered')) {
							container.classList.remove('catalog__products_slider__list_centered');
							slider.params.centeredSlides = false;
							slider.params.centeredSlidesBounds = false;
							slider.update();
						}
					}
				}
			});
		} else {
			app.sliders.forEach(slider => {
				if (slider.initialized === true) {
					let container = slider.el;
					if (container && container.classList.contains('catalog__products_slider__list') && container.closest('.catalog__products_gallery_swap')) {
						if (!container.classList.contains('catalog__products_slider__list_centered')) {
							container.classList.add('catalog__products_slider__list_centered');
							slider.params.centeredSlides = true;
							slider.params.centeredSlidesBounds = true;
							slider.update();
						}
					}
				}
			});
		}
	},

	/**
	 * @param button
	 */
	showBtnLoading: function (button) {
		let showLoader = (btn, callback) => {
				btn.style.width = `${btn.offsetWidth}px`;
				btn.style.height = `${btn.offsetHeight}px`;
				btn.disabled = true;
				btn.classList.add('loading');

				callback;
			},
			hideLoader = (btn) => {
				btn.classList.remove('loading');
				btn.removeAttribute('style');
				btn.disabled = false;
			};

		showLoader(button, button.addEventListener('_loaded', function loadedHandler(e) {
			hideLoader(e.target);
			e.target.removeEventListener('_loaded', loadedHandler);
		}));
	},

	/**
	 * @param block
	 * @param clearContent
	 */
	showBlockLoading: function (block, clearContent = true) {
		let showLoader = (block, callback) => {
				setTimeout(() => block.classList.add('shown'), 0);
				setTimeout(() => block.classList.add('loading'), 1);
				if (clearContent) {
					block.innerHTML = '';
				}
				callback
			},
			hideLoader = (block) => {
				setTimeout(() => block.classList.remove('loading'), 300);
				setTimeout(() => block.classList.remove('shown'), 500);
			};

		showLoader(block, block.addEventListener('_loaded', function loadedHandler(e) {
			hideLoader(e.target);
			e.target.removeEventListener('_loaded', loadedHandler);
		}));
	},

	/**
	 * Показать глобальный лоадер
	 * @param {string} closeEvent - событие для автоматического скрытия лоадера
	 */
	showLoader: function (closeEvent) {
		let loader = document.createElement('div');

		loader.classList.add('body_spinner');
		loader.innerHTML = '<svg class="logo__icon"><use href="#logo.svg"></use></svg><div class="spinner"></div>';
		document.body.prepend(loader);
		setTimeout(() => loader.classList.add('active'), 0);

		if (closeEvent) {
			(async () => {
				await new Promise(resolve => document.addEventListener(closeEvent, resolve, {once: true}));
			})().then(() => {
				loader.classList.remove('active');
				setTimeout(() => loader.remove(), 200);
			});
		}
	},

	/**
	 * @param popupData
	 * @param bodyActiveClass
	 * @param mobilePaneClass
	 * @param options {{fullHeight?: boolean}}
	 * @returns {Promise<*>}
	 */
	initCoopertinoPane: function (popupData, bodyActiveClass = 'modal-open', mobilePaneClass, options = {}) {
		let mobileContainer = document.createElement('div');

		//Если есть специализированный класс панели (например, quick_buy) — удаляем предыдущие контейнеры этого типа
		if (mobilePaneClass) {
			let existing = document.querySelectorAll('.' + mobilePaneClass);
			if (existing && existing.length) existing.forEach(el => el.remove());
		}

		mobileContainer.classList.add('mobile-pane__container');
		if (mobilePaneClass) mobileContainer.classList.add(mobilePaneClass);

		document.body.appendChild(mobileContainer);
		mobileContainer.append(popupData);

		return Router.assets.CupertinoPane().then(module => {
			if (!window.CupertinoPane) window.CupertinoPane = module.CupertinoPane;

			//fullHeight: длинные sitePage (warranty/FAQ) — top-break без fitHeight, иначе схлопывание как 1545
			let paneSettings = options.fullHeight
				? {initialBreak: 'top', bottomClose: true, lowerThanBottom: false}
				: {fitHeight: true, maxFitHeight: window.innerHeight - 40, bottomClose: true, lowerThanBottom: false};

			let _modal = new CupertinoPane(mobileContainer, buildCupertinoPaneSettings(paneSettings));

			//Сохраняем ссылку на инстанс панели на контейнере, чтобы другие модули могли корректно её закрывать
			mobileContainer._pane = _modal;

			//Событие перед открытием панели
			_modal.on('onWillPresent', () => {
				document.body.classList.add(bodyActiveClass);

				//Добавляем кастомный класс pane только для быстрой покупки
				if (mobileContainer.classList.contains('mobile-pane__container__quick_buy')) {
					setTimeout(() => {
						let pane = mobileContainer.closest('.pane');
						if (pane) pane.classList.add('pane_fast_buy');
					}, 0);
				}
			});

			//Событие перед закрытием панели
			_modal.on('onWillDismiss', () => {
				document.body.classList.remove(bodyActiveClass);
			});

			//После полного закрытия — удаляем контейнер из DOM и чистим класс на .pane
			_modal.on('onDidDismiss', () => {
				let pane = mobileContainer.closest('.pane');
				if (pane) pane.classList.remove('pane_fast_buy');
				if (mobileContainer && mobileContainer.parentNode) mobileContainer.remove();
			});

			//Клик по бэкдропу
			_modal.on('onBackdropTap', () => {
				document.body.classList.remove(bodyActiveClass);
				_modal.hide({animate: true});
			});

			//Закрытие по кнопке (aside template — offcanvas, модалки — modal)
			let closeBtns = mobileContainer.querySelectorAll('[data-bs-dismiss=offcanvas], [data-bs-dismiss=modal]');
			if (closeBtns) closeBtns.forEach(_btn => {
				_btn.addEventListener('click', () => {
					if (_modal) _modal.hide({animate: true})
				});
			});

			return Promise.resolve(_modal.present({animate: true})).then(() => _modal);
		});
	},

	/**
	 * Нормализация заголовка aside: trim, схлопывание пробелов, без регистра
	 * @param {string} text
	 * @returns {string}
	 */
	normalizeAsideTitle: function (text) {
		return String(text || '').replace(/\s+/g, ' ').trim().toLowerCase();
	},

	/**
	 * Обёртка заголовка пуста, если нет текста и медиа после удаления h1/h2
	 * @param {Element} wrapper
	 * @returns {boolean}
	 */
	isEmptyAsideTitleWrapper: function (wrapper) {
		if (!wrapper || wrapper.querySelector('img, svg, iframe, video, picture, canvas')) return false;

		return !this.normalizeAsideTitle(wrapper.textContent);
	},

	/**
	 * В aside заголовок уже в offcanvas-title — убираем тот же h1/h2 из article
	 * @param {Element} article
	 * @param {string} title
	 */
	stripDuplicateAsideTitle: function (article, title) {
		let normalizedTitle = this.normalizeAsideTitle(title);
		if (!article || !normalizedTitle) return;

		let headings = article.querySelectorAll('h1, h2, .page__article_h2, .page__title, .page__article_title');
		for (let heading of headings) {
			if (this.normalizeAsideTitle(heading.textContent) !== normalizedTitle) continue;

			let wrapper = heading.parentElement;
			heading.remove();

			if (wrapper && wrapper !== article && this.isEmptyAsideTitleWrapper(wrapper)) {
				wrapper.remove();
			}

			break;
		}
	},

	/**
	 * @param html
	 * @returns {string|boolean}
	 */
	buildAsideTemplate: function (html) {
		let _div = document.createElement('div');
		_div.innerHTML = html;

		let banner = _div.querySelector('[data-role=page-banner]'),
			content = _div.querySelector('[data-role=page-article]');

		if (!content) return false;

		let title = content.getAttribute('data-title'),
			id = content.getAttribute('data-id'),
			closeLabel = this.getMessage('close'),
			article = content.cloneNode(true);

		if (title) this.stripDuplicateAsideTitle(article, title);

		if (!id) id = '_' + Math.random().toString(36).substr(2, 8);

		let template = '<div class="offcanvas offcanvas-end" tabindex="-1" id="' + id + '" aria-labelledby="' + id + '">\n' +
			'  <div data-role="modal-content">\n' +
			'    <div class="offcanvas-header">\n' +
			(title ? '      <div class="offcanvas-title">' + title + '</div>' : '') +
			'      <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="' + closeLabel + '" title="' + closeLabel + '"></button>\n' +
			'    </div>\n' +
			'    <div class="offcanvas-body">\n' +
			(banner ? '<div class="offcanvas-banner">' + banner.innerHTML + '</div>' : '') +
			'<div class="offcanvas-content">' + article.innerHTML + '</div>' +
			'    </div>\n' +
			'  </div>\n' +
			'</div>';

		return template;
	},

	/**
	 * @param path
	 * @param type
	 * @param params
	 * @param showModal
	 * @returns {Promise<unknown>}
	 */
	loadAjaxAside: function (path, type = 'sitePage', params, showModal = true) {
		let app = this,
			api = app.api,
			modalHash = encodeURI(app.serialize({path: path, type: type, params: params, showModal: showModal}));

		let parsedParams = params;
		if (typeof params === 'string') {
			try {
				parsedParams = app.unserialize(decodeURIComponent(params)) || {};
			} catch (e) {
				parsedParams = {};
			}
		}
		parsedParams = parsedParams || {};

		return new Promise((resolve, reject) => {
			if (app.modals[modalHash]) {
				let _modal = app.modals[modalHash];
				if ('show' in _modal && typeof _modal.show === 'function') {
					_modal.show();
					let modal = _modal._element;
					if (modal) return resolve(modal);
				} else {
					_modal.present({animate: true});
					let modal = _modal;

					//Если передан параметр anchor, прокручиваем к нему
					if (parsedParams && 'anchor' in parsedParams) {
						let _scrollcontainer = document.querySelector('.mobile-pane__container [data-role=modal-content]');
						if (_scrollcontainer) {
							let el = _scrollcontainer.querySelector('#' + CSS.escape(parsedParams.anchor));
							if (el) setTimeout(() => el.scrollIntoView({behavior: 'smooth', block: 'start'}), 300);
						}
					}

					if (modal) return resolve(modal);
				}
			} else {
				this.api.loadAjaxModal(path, (type === 'sitePage' ? 'text' : 'json'), params).then((result) => {
					let template, offcanvas;
					switch (type) {
						case 'sitePage':
							template = app.buildAsideTemplate(result);
							break;
						default:
							if ('template' in result && result.template) {
								template = result.template;
							}
							break;
					}

					if (template) {
						let div = document.createElement('div');
						div.innerHTML = template;

						offcanvas = div.querySelector('.offcanvas');
						if (offcanvas) {
							if (showModal) {
								let _offcanvas;
								if (window.innerWidth >= app.breakpoint) {
									//Если это вложенный aside из quick_buy - не закрываем quick_buy
									if (params && params._fromQuickBuy) { //Добавляем класс для стилизации вложенного aside
										offcanvas.classList.add('offcanvas-nested');
									}

									document.body.appendChild(offcanvas);
									_offcanvas = new bootstrap.Offcanvas(offcanvas);
									_offcanvas.show();
								} else {
									let popupData = offcanvas.querySelector('[data-role=modal-content]');
									if (popupData) {
										let mobilePaneClass = (path === 'product-quick-buy' ? 'mobile-pane__container__quick_buy' : undefined);
										app.initCoopertinoPane(popupData, undefined, mobilePaneClass, {fullHeight: type === 'sitePage'}).then((pane) => {
											app.modals[modalHash] = pane;

											//Если передан параметр anchor, прокручиваем к нему
											if (parsedParams && 'anchor' in parsedParams) {
												let _scrollcontainer = document.querySelector('.mobile-pane__container [data-role=modal-content]');
												if (_scrollcontainer) {
													let el = _scrollcontainer.querySelector('#' + CSS.escape(parsedParams.anchor));
													if (el) setTimeout(() => el.scrollIntoView({
														behavior: 'smooth',
														block: 'start'
													}), 300);
												}
											}
										});
									}
								}

								if (_offcanvas) app.modals[modalHash] = _offcanvas;
							}
						} //else reject({status: 404, response: 'Модальное окно не найдено'});
						div.remove();
					}

					let dataTypes = ['script', 'storage'];
					for (let i in dataTypes) {
						if (dataTypes.hasOwnProperty(i)) {
							let dataType = dataTypes[i];
							if (result[dataType]) {
								switch (dataType) {
									case 'storage':
										for (let type in result[dataType])
											if (result[dataType].hasOwnProperty(type))
												app.storage.set(type, result[dataType][type]);
										break;
									case 'script':
										setTimeout(() => eval(result[dataType]), 200);
										break;
									default:
										break;
								}
							}
						}
					}

					if (offcanvas) return resolve(offcanvas);
				});
			}

			return false;
		}).then(response => {

			return response;
		});
	},

	/**
	 * @param path
	 * @param params
	 * @param showModal
	 * @returns {Promise<unknown>}
	 */
	loadAjaxModal: function (path, params, showModal = true, addToCache = false, cacheType = 'runtime') {
		let app = this,
			modalHash = encodeURI(app.serialize({path: path, params: params, showModal: showModal}));

		return new Promise((resolve, reject) => {
			if (app.modals[modalHash]) {
				let _modal = app.modals[modalHash];
				if ('show' in _modal && typeof _modal.show === 'function') {
					_modal.show();
					let modal = _modal._element;
					if (modal) return resolve(modal);
				} else {
					_modal.present({animate: true});
					let modal = _modal;
					if (modal) return resolve(modal);
				}
			} else {
				this.api.loadAjaxModal(path, 'json', params, true, addToCache, cacheType).then((result) => {
					let modal,
						dataTypes = ['html', 'script', 'storage'];

					for (let i in dataTypes) {
						if (dataTypes.hasOwnProperty(i)) {
							let dataType = dataTypes[i];
							if (result[dataType]) {
								switch (dataType) {
									case 'html':
										let div = document.createElement('div');
										div.innerHTML = result[dataType];

										modal = div.querySelector('.modal');
										if (modal) {
											if (showModal) {
												let _modal;
												if (window.innerWidth >= app.breakpoint || path === 'auth') {
													document.body.appendChild(modal);
													_modal = new bootstrap.Modal(modal);
													_modal.show();
												} else {
													let popupData = modal.querySelector('[data-role=modal-content]');
													if (popupData) {
														app.initCoopertinoPane(popupData).then((pane) => {
															app.modals[modalHash] = pane;
														});
													}
												}

												if (_modal) app.modals[modalHash] = _modal;
											}

											//Инициализация JS логики для модального окна авторизации
											if (path === 'auth') {
												let authContainer = modal.querySelector('[data-modal-role=auth]');
												if (authContainer) {
													/*if (!app.auth) app.auth = {};
													let templateName = authContainer.getAttribute('data-auth-template') || 'popup';
													if (!app.auth[templateName]) app.auth[templateName] = new AppAuthorization(app);
													//Обработчик для перехода на checkout добавляется в AppAuthorization.js
													//Здесь только добавляем класс для стилизации*/
													if (location.pathname === '/cart/') modal.classList.add('cart_modal');
												}

												//Обновляем иконку авторизации в мобильном footer
												if (result.htmlFooter) {
													let footerAuthContainer = document.querySelector('[data-role=footer-auth-container]');
													if (footerAuthContainer) {
														footerAuthContainer.innerHTML = result.htmlFooter;
													} else {
														//Ищем контейнер по содержимому (иконка авторизации)
														let tapbarItems = document.querySelectorAll('.mobile_tapbar__item');
														for (let item of tapbarItems) {
															let authLink = item.querySelector('[data-toggle="ajax-modal"][data-path="auth"], [data-bs-toggle="modal"][data-bs-target="#login-or-register"]');
															if (authLink) {
																item.innerHTML = result.htmlFooter;
																break;
															}
														}
													}
												}
											}
										} //else reject({status: 404, response: 'Модальное окно не найдено'});
										div.remove();
										break;
									case 'storage':
										for (let type in result[dataType])
											if (result[dataType].hasOwnProperty(type))
												app.storage.set(type, result[dataType][type]);
										break;
									case 'script':
										setTimeout(() => eval(result[dataType]), 200);
										break;
									default:
										break;
								}
							}
						}
					}

					if (modal) return resolve(modal);
				});
			}

			return false;
		}).then(response => {

			return response;
		});
	},

	/**
	 *
	 * @param path
	 * @param params
	 * @returns {Promise<unknown>}
	 */
	loadMore: function (path, params) {
		let app = this;

		return new Promise((resolve, reject) => {
			this.api.loadMore(path, params).then((result) => {
				let container,
					_params = app.unserialize(decodeURIComponent(params)),
					dataTypes = ['html', 'script', 'storage'];

				for (let i in dataTypes) {
					if (dataTypes.hasOwnProperty(i)) {
						let dataType = dataTypes[i];
						if (result[dataType]) {

							switch (dataType) {
								case 'html':
									let div = document.createElement('div');
									div.innerHTML = result[dataType];

									if (_params.itemsWrapp) {
										let itemsContent = document.querySelector(_params.itemsWrapp),
											itemsContentNew = div.querySelector(_params.itemsWrapp);

										container = itemsContent;

										if (itemsContent && itemsContentNew) {
											itemsContent.insertAdjacentHTML('beforeend', itemsContentNew.innerHTML);
										}
									}

									let currentShowMore = document.querySelector('[data-role=load-more]'),
										newShowMore = div.querySelector('[data-role=load-more]');

									if (!newShowMore) {
										let wrapp = currentShowMore.closest('[data-role=load-more-wrapp]');
										wrapp ? wrapp.remove() : currentShowMore.remove();
									} else {
										currentShowMore.setAttribute('data-params', newShowMore.getAttribute('data-params'));
									}

									div.remove();
									break;
								case 'storage':
									for (let type in result[dataType])
										if (result[dataType].hasOwnProperty(type))
											app.storage.set(type, result[dataType][type]);
									break;
								case 'script':
									setTimeout(() => eval(result[dataType]), 200);
									break;
								default:
									break;
							}
						}
					}
				}

				if (container) return resolve(container);
				else reject({response: 'Контейнер не найден'});
			});

			return false;
		}).then(response => {

			return response;
		});
	},

	/**
	 * @param path
	 * @param params
	 * @returns {*}
	 */
	loadAndShowAjaxModal: function (path, params) {
		let app = this,
			api = app.api,
			activeClasses = {
				body: 'body-overflow-hidden'
			};

		return api.loadAjaxModal(path, params).then((result) => {
			if (result.html) {
				let div = document.createElement('div');
				div.innerHTML = result.html;
				div.id = 'modal_' + path;

				let body = document.body;
				body.append(div);
				body.classList.add(activeClasses.body);

				app.delegate(document, 'click', '[data-dismiss=modal]', () => {
					let modal = body.querySelector('#' + div.id);

					if (modal) modal.remove();
					body.classList.remove(activeClasses.body);
				});
			}
		});
	},

	/**
	 * @returns {{"[": string, "]": string, "`": string, a: string, b: string, c: string, d: string, e: string, f: string, g: string, "'": string, h: string, i: string, j: string, k: string, l: string, ",": string, m: string, n: string, ".": string, o: string, p: string, q: string, r: string, s: string, t: string, u: string, v: string, w: string, x: string, y: string, z: string, ";": string}}
	 */
	getAutoCompleteReplaceMap: function () {
		return {
			'a': 'ф',
			'b': 'и',
			'c': 'с',
			'd': 'в',
			'e': 'у',
			'f': 'а',
			'g': 'п',
			'h': 'р',
			'i': 'ш',
			'j': 'о',
			'k': 'л',
			'l': 'д',
			'm': 'ь',
			'n': 'т',
			'o': 'щ',
			'p': 'з',
			'q': 'й',
			'r': 'к',
			's': 'ы',
			't': 'е',
			'u': 'г',
			'v': 'м',
			'w': 'ц',
			'x': 'ч',
			'y': 'н',
			'z': 'я',
			'`': 'ё',
			'[': 'х',
			']': 'ъ',
			';': 'ж',
			'\'': 'э',
			',': 'б',
			'.': 'ю',
		};
	},

	/**
	 * @param query
	 * @param reverse
	 * @returns {string}
	 */
	prepareAutoCompleteQuery: function (query, reverse = false) {
		if (!query.length) return;

		let val = '',
			replace = this.getAutoCompleteReplaceMap();

		if (reverse) {
			replace = Object.fromEntries(
				Object.entries(replace).map(([key, value]) => [value, key])
			)
		}

		for (let i = 0; i < query.length; i++) {
			if (replace[query.charAt(i).toLowerCase()]) {
				val += replace[query.charAt(i).toLowerCase()];
			} else {
				val += query.charAt(i);
			}
		}

		return val;
	},

	/**
	 * void
	 */
	updateBodyPadding: function () {
		let body = document.body;

		if (body.classList.contains('modal-open')) {
			document.body.style.position = 'sticky';
			document.body.style.overflow = 'hidden';
			document.body.style.left = '0';
			document.body.style.right = '0';
			//document.body.style.paddingRight = '0';
		} else {
			document.body.style.position = '';
			document.body.style.overflow = '';
			document.body.style.left = '';
			document.body.style.right = '';
			//document.body.style.paddingRight = '';
		}
	}
}